import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Edit2, Trash2, RefreshCw, X } from 'lucide-react';
import './AdminCategoriesManagement.css';
import { API_BASE_URL } from '../../api';

const AdminCategoriesManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    gst_rate: '', 
    hsn_code: ''
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null); // Store category ID for inline confirmation
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const initialNewCategoryState = { name: '', gst_rate: '', hsn_code: '' };
  const [newCategory, setNewCategory] = useState(initialNewCategoryState);
  const [newCategoryImage, setNewCategoryImage] = useState(null);
  const [newCategoryPreview, setNewCategoryPreview] = useState(null);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const newCategoryImageInputRef = useRef(null);

  // Toast notifications
  const [toasts, setToasts] = useState([]);
  const showToast = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  };

  // Get admin headers
  const adminHeaders = () => {
    const adminToken = window.localStorage.getItem('adminToken');
    const vendorToken = window.localStorage.getItem('token');
    const legacyKey = window.localStorage.getItem('ADMIN_API_KEY') || window.localStorage.getItem('adminApiKey');
    const headers = { 'Content-Type': 'application/json' };
    if (adminToken) headers['Authorization'] = `Bearer ${adminToken}`;
    else if (vendorToken) headers['Authorization'] = `Bearer ${vendorToken}`;
    if (legacyKey) headers['x-admin-key'] = legacyKey;
    return headers;
  };

  const adminAuthHeaders = () => {
    const headers = {};
    const adminToken = window.localStorage.getItem('adminToken');
    const vendorToken = window.localStorage.getItem('token');
    const legacyKey = window.localStorage.getItem('ADMIN_API_KEY') || window.localStorage.getItem('adminApiKey');
    if (adminToken) headers['Authorization'] = `Bearer ${adminToken}`;
    else if (vendorToken) headers['Authorization'] = `Bearer ${vendorToken}`;
    if (legacyKey) headers['x-admin-key'] = legacyKey;
    return headers;
  };

  // Fetch all categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching categories from /api/categories');
      const res = await fetch(`${API_BASE_URL}/api/categories`, { headers: adminHeaders() });
      console.log('Response status:', res.status, res.statusText);
      
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const errorMsg = data.error || data.message || `Failed to fetch categories (${res.status})`;
        console.error('Error response:', data);
        throw new Error(errorMsg);
      }
      const data = await res.json();
      console.log('Categories fetched:', data.categories?.length || 0);
      // Log image paths for debugging
      data.categories?.forEach((cat, index) => {
        if (cat.category_image) {
          console.log(`Category ${index + 1} (${cat.name}): image = ${cat.category_image}`);
        }
      });
      setCategories(data.categories || []);
    } catch (e) {
      console.error('Error in fetchCategories:', e);
      const errorMsg = e.message || 'Failed to fetch categories';
      setError(errorMsg);
      showToast(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Open edit form inline
  const handleEdit = (category) => {
    // If clicking edit on the same category, close it
    if (editingCategoryId === category.id) {
      closeEditForm();
      return;
    }
    setEditingCategoryId(category.id);
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      gst_rate: category.gst_rate !== null && category.gst_rate !== undefined ? category.gst_rate : '',
      hsn_code: category.hsn_code || ''
    });
    setSelectedImage(null);
    setImagePreview(category.category_image || null);
  };

  // Close edit form function
  const closeEditForm = useCallback(() => {
    if (updating) return; // Prevent closing during update
    const currentEditingId = editingCategoryId;
    setEditingCategoryId(null);
    setEditingCategory(null);
    setFormData({ name: '', gst_rate: '', hsn_code: '' });
    setSelectedImage(null);
    setImagePreview(null);
    // Clear file input after state is cleared
    setTimeout(() => {
      if (currentEditingId) {
        const fileInput = document.getElementById(`category-image-input-${currentEditingId}`);
        if (fileInput) {
          fileInput.value = '';
        }
      }
    }, 0);
  }, [updating, editingCategoryId]);

  // Close on Escape key
  useEffect(() => {
    if (editingCategoryId && !updating) {
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          closeEditForm();
        }
      };
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [editingCategoryId, updating, closeEditForm]);

  // Handle image file selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match('image.*')) {
        showToast('Please select a valid image file', 'error');
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        showToast('Image size should be less than 5MB', 'error');
        return;
      }
      
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNewCategoryImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    if (!file.type.match('image.*')) {
      showToast('Please select a valid image file', 'error');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast('Image size should be less than 5MB', 'error');
      return;
    }

    setNewCategoryImage(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setNewCategoryPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const resetNewCategoryForm = () => {
    setNewCategory(initialNewCategoryState);
    setNewCategoryImage(null);
    setNewCategoryPreview(null);
    if (newCategoryImageInputRef.current) {
      newCategoryImageInputRef.current.value = '';
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    const trimmedName = newCategory.name.trim();
    if (!trimmedName) {
      showToast('Category name is required', 'error');
      return;
    }

    try {
      setCreatingCategory(true);
      const formDataToSend = new FormData();
      formDataToSend.append('name', trimmedName);
      formDataToSend.append('gst_rate', newCategory.gst_rate || '');
      formDataToSend.append('hsn_code', newCategory.hsn_code || '');
      if (newCategoryImage) {
        formDataToSend.append('category_image', newCategoryImage);
      }

      const res = await fetch(`${API_BASE_URL}/api/categories`, {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: formDataToSend
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || data.message || 'Failed to create category');
      }

      resetNewCategoryForm();
      fetchCategories();
      showToast('Category created successfully', 'success');
    } catch (err) {
      console.error('Error creating category:', err);
      showToast(err.message || 'Failed to create category', 'error');
    } finally {
      setCreatingCategory(false);
    }
  };

  // Handle update
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingCategory) return;

    try {
      setUpdating(true);
      
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('gst_rate', formData.gst_rate || '');
      formDataToSend.append('hsn_code', formData.hsn_code || '');
      
      // Add image file if selected
      if (selectedImage) {
        console.log('Adding image to FormData:', selectedImage.name, selectedImage.size, selectedImage.type);
        formDataToSend.append('category_image', selectedImage);
      } else {
        console.log('No image selected for upload');
      }
      
      // Get headers without Content-Type (let browser set it for FormData)
      const headers = adminAuthHeaders();
      
      console.log('Sending update request with FormData...');
      const res = await fetch(`${API_BASE_URL}/api/categories/${editingCategory.id}`, {
        method: 'PUT',
        headers: headers,
        body: formDataToSend
      });

      console.log('Response status:', res.status);
      
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.error('Update failed:', data);
        throw new Error(data.error || data.details || 'Failed to update category');
      }

      const result = await res.json();
      console.log('Update successful:', result);
      
      // Reset form and close edit form
      closeEditForm();
      
      fetchCategories(); // Refresh list
      showToast('Category updated successfully', 'success');
    } catch (e) {
      console.error('Error in handleUpdate:', e);
      showToast(e.message, 'error');
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = (category) => {
    console.log('handleDeleteClick called for category:', category);
    // Set the category ID to show inline confirmation
    setCategoryToDelete(category.id);
  };

  // Cancel delete confirmation
  const cancelDelete = () => {
    setCategoryToDelete(null);
  };

  // Handle delete
  const handleDelete = async (categoryId) => {
    if (!categoryId) {
      console.error('No category ID provided for deletion');
      return;
    }

    try {
      setDeleting(true);
      console.log('Deleting category:', categoryId);
      
      const res = await fetch(`${API_BASE_URL}/api/categories/${categoryId}`, {
        method: 'DELETE',
        headers: adminHeaders()
      });

      console.log('Delete response status:', res.status, res.statusText);

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.error('Delete failed:', data);
        const errorMsg = data.error || data.message || `Failed to delete category (${res.status})`;
        throw new Error(errorMsg);
      }

      const result = await res.json();
      console.log('Delete successful:', result);

      setCategoryToDelete(null);
      fetchCategories(); // Refresh list
      showToast('Category deleted successfully', 'success');
    } catch (e) {
      console.error('Error in handleDelete:', e);
      showToast(e.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="admin-categories">
      <div className="admin-categories-header">
        <div className="admin-categories-header-content">
          <h1>Categories Management</h1>
          <p>Manage product categories</p>
        </div>
        <button className="btn btn-secondary" onClick={fetchCategories} disabled={loading}>
          <RefreshCw size={20} className={loading ? 'spinning' : ''} />
          Refresh
        </button>
      </div>

      {/* Toast notifications */}
      {!!toasts.length && (
        <div className="toast-container">
          {toasts.map(t => (
            <div key={t.id} className={`toast toast-${t.type}`}>
              {t.message}
            </div>
          ))}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <div className="category-create-card">
        <div className="category-create-header">
          <div>
            <h2>Add New Category</h2>
            <p>Store the category name, GST %, HSN code, and display image directly in the catalog.</p>
          </div>
        </div>
        <form className="category-create-form" onSubmit={handleCreateCategory}>
          <div className="form-row">
            <div className="form-group">
              <label>Category Name *</label>
              <input
                type="text"
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                placeholder="e.g., Fresh Produce"
                required
                disabled={creatingCategory}
              />
            </div>
            <div className="form-group">
              <label>GST Rate (%) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="28"
                value={newCategory.gst_rate}
                onChange={(e) => setNewCategory({ ...newCategory, gst_rate: e.target.value })}
                placeholder="e.g., 5, 12, 18"
                required
                disabled={creatingCategory}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>HSN Code</label>
              <input
                type="text"
                value={newCategory.hsn_code}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  if (value.length <= 8) {
                    setNewCategory({ ...newCategory, hsn_code: value });
                  }
                }}
                maxLength="8"
                placeholder="Enter HSN code"
                disabled={creatingCategory}
              />
            </div>
            <div className="form-group">
              <label>Category Image</label>
              <div className="file-upload-control">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => !creatingCategory && newCategoryImageInputRef.current?.click()}
                  disabled={creatingCategory}
                >
                  Choose File
                </button>
                <span className="file-upload-label">
                  {newCategoryImage ? newCategoryImage.name : 'No file chosen'}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleNewCategoryImageChange}
                  disabled={creatingCategory}
                  className="file-input-hidden"
                  ref={newCategoryImageInputRef}
                />
              </div>
              <small className="file-hint">Upload JPG, PNG, GIF or WEBP (Max 5MB)</small>
            </div>
          </div>
          {newCategoryPreview && (
            <div className="category-create-preview">
              <img src={newCategoryPreview} alt="New category" />
              {newCategoryImage && (
                <div className="preview-info">
                  Selected image: {newCategoryImage.name} ({(newCategoryImage.size / 1024).toFixed(2)} KB)
                </div>
              )}
            </div>
          )}
          <div className="category-create-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={resetNewCategoryForm}
              disabled={creatingCategory}
            >
              Clear
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={creatingCategory}
            >
              {creatingCategory ? 'Saving...' : 'Add Category'}
            </button>
          </div>
        </form>
      </div>

      <div className="admin-categories-table-container">
        <table className="admin-categories-table">
          <thead>
            <tr>
              <th>Category Name</th>
              <th>GST (%)</th>
              <th>HSN Code</th>
              <th>Category Image</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} style={{ padding: 24, textAlign: 'center' }}>
                  Loading...
                </td>
              </tr>
            )}
            {!loading && categories.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: 24, textAlign: 'center' }}>
                  No categories found
                </td>
              </tr>
            )}
            {!loading && categories.map(category => (
              <React.Fragment key={category.id}>
                {/* Inline Edit Form - appears above the row when editing */}
                {editingCategoryId === category.id && (
                  <tr className="inline-edit-form-row">
                    <td colSpan={5} className="inline-edit-form-cell">
                      <div className="inline-edit-form-container">
                        <div className="inline-edit-form-header">
                          <h3>Edit Category: {category.name}</h3>
                          <button 
                            className="close-inline-btn" 
                            onClick={closeEditForm}
                            disabled={updating}
                            aria-label="Close edit form"
                          >
                            <X size={18} />
                          </button>
                        </div>
                        <form onSubmit={handleUpdate} className="inline-edit-form">
                          <div className="form-row">
                            <div className="form-group">
                              <label>Category Name *</label>
                              <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                disabled={updating}
                              />
                            </div>
                            <div className="form-group">
                              <label>GST Rate (%) *</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="28"
                                value={formData.gst_rate}
                                onChange={(e) => setFormData({ ...formData, gst_rate: e.target.value })}
                                required
                                disabled={updating}
                                placeholder="e.g., 5, 12, 18"
                              />
                            </div>
                            <div className="form-group">
                              <label>HSN Code</label>
                              <input
                                type="text"
                                value={formData.hsn_code}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/[^0-9]/g, '');
                                  if (value.length <= 8) {
                                    setFormData({ ...formData, hsn_code: value });
                                  }
                                }}
                                maxLength="8"
                                disabled={updating}
                                placeholder="Enter HSN code"
                              />
                            </div>
                          </div>
                          <div className="form-group">
                            <label>Category Image</label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              disabled={updating}
                              className="file-input"
                              id={`category-image-input-${category.id}`}
                            />
                            <small className="file-hint">Select an image file (JPG, PNG, GIF, WEBP - Max 5MB)</small>
                            {imagePreview && (
                              <div className="image-preview">
                                <img 
                                  src={imagePreview} 
                                  alt="Preview"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'block';
                                  }}
                                />
                                <div className="preview-error" style={{ display: 'none' }}>
                                  Invalid image
                                </div>
                                {selectedImage && (
                                  <div className="preview-info">
                                    New image selected: {selectedImage.name} ({(selectedImage.size / 1024).toFixed(2)} KB)
                                  </div>
                                )}
                                {!selectedImage && editingCategory?.category_image && (
                                  <div className="preview-info" style={{ background: '#f3f4f6', color: '#6b7280' }}>
                                    Current image (no change)
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="inline-edit-form-actions">
                            <button 
                              type="button" 
                              className="btn btn-secondary" 
                              onClick={closeEditForm}
                              disabled={updating}
                            >
                              Cancel
                            </button>
                            <button 
                              type="submit" 
                              className="btn btn-primary" 
                              disabled={updating}
                            >
                              {updating ? 'Updating...' : 'Update Category'}
                            </button>
                          </div>
                        </form>
                      </div>
                    </td>
                  </tr>
                )}
                {/* Regular Category Row */}
                <tr className={editingCategoryId === category.id ? 'row-being-edited' : ''}>
                  <td>
                    <div className="category-name-cell">
                      <strong>{category.name || 'N/A'}</strong>
                      {category.description && (
                        <div className="category-description">{category.description}</div>
                      )}
                    </div>
                  </td>
                  <td>{category.gst_rate !== null && category.gst_rate !== undefined ? `${category.gst_rate}%` : '-'}</td>
                  <td>{category.hsn_code || '-'}</td>
                  <td>
                    {category.category_image && category.category_image.trim() !== '' ? (
                      <img 
                        src={category.category_image} 
                        alt={category.name}
                        className="category-image"
                        onError={(e) => {
                          console.error('Image load error for:', category.category_image);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                        onLoad={() => {
                          console.log('Image loaded successfully:', category.category_image);
                        }}
                      />
                    ) : null}
                    <div className="no-image-placeholder" style={{ display: (category.category_image && category.category_image.trim() !== '') ? 'none' : 'flex' }}>
                      <span>No Image</span>
                    </div>
                  </td>
                  <td>
                    <div className="admin-category-actions">
                      {categoryToDelete === category.id ? (
                        <div className="delete-confirmation-inline">
                          <div className="delete-confirm-message">
                            <p>Delete <strong>"{category.name}"</strong>?</p>
                            <p className="warning-text">This cannot be undone.</p>
                          </div>
                          <div className="delete-confirm-buttons">
                            <button 
                              type="button"
                              className="btn btn-secondary btn-sm" 
                              onClick={cancelDelete}
                              disabled={deleting}
                            >
                              Cancel
                            </button>
                            <button 
                              type="button"
                              className="btn btn-danger btn-sm" 
                              onClick={() => handleDelete(category.id)}
                              disabled={deleting}
                            >
                              {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button 
                            type="button"
                            className={`admin-action-btn edit ${editingCategoryId === category.id ? 'active' : ''}`}
                            title={editingCategoryId === category.id ? 'Close Edit' : 'Edit'} 
                            onClick={() => handleEdit(category)}
                          >
                            <Edit2 size={16} />
                          </button>
                          <button 
                            type="button"
                            className="admin-action-btn delete" 
                            title="Delete" 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log('Delete button clicked for category:', category.id, category.name);
                              handleDeleteClick(category);
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>


    </div>
  );
};

export default AdminCategoriesManagement;

